<template>
    <div class="eml-emails-list">
        <emails-list-controls-bar :controls-enabled="table.checkedRows.length" :indeterminate="isSelectAllIndeterminate"
            :markread-enabled="markreadEnabled" :markunread-enabled="markunreadEnabled" :pagination="pagination"
            :select-all-checkbox="isSelectAllActive" @checkbox="handle('selectToggle')" @delete="handle('delete')"
            @markread="handle('markread')" @markunread="handle('markunread')"
            @pagination-change="handlePaginationChange" @refresh="handle('refresh')" />
        <o-table v-model:checked-rows="table.checkedRows" @click="handleRowClick"
            :class="['eml-emails-list-table', { queried: !!this.emails_query }]" :data="messages"
            :is-row-checkable="() => true" :row-class="rowClass" checkable>
            <template>
                <o-table-column #default="{ row }" class="column-from" field="from">
                    <span v-for="(item, key) in row.from_array" :class="{ title: item.read }" :key="key">{{ item.name
                        }}{{
                            (row.from_array.length !== key + 1 &&
                                `,&nbsp;`) ||
                            ""
                        }}
                    </span>
                    &nbsp;
                    <span class="count">{{ row.from_names_count }}</span>
                </o-table-column>
                <o-table-column #default="{ row }" field="subject" sortable>
                    <span class="title is-6">{{ row.subject }}</span>
                    &nbsp;-&nbsp;
                    <span class="snippet break-word" v-html="row.snippet" />
                    <div class="msg-attachments" v-if="row.files && row.files.length">
                        <span class="msg-attachment" v-for="(file, key) in row.files" :key="`files-${key}`"
                            @click.stop="handle_open_file(file)">
                            <a :href="file.path" target="_blank" @click.prevent.stop="handle_open_file(file)">{{
                                file.name }}
                            </a>
                        </span>
                    </div>
                </o-table-column>
                <o-table-column #default="{ row }" field="date" sortable>
                    <div class="title" style="white-space: nowrap">{{ getDate(row.date) }}</div>
                </o-table-column>
            </template>
            <template #empty>
                <section class="section">
                    <div class="content has-text-grey has-text-centered">
                        <p><o-icon icon="envelope" size="is-large" /></p>
                        <p>No emails here.</p>
                    </div>
                </section>
            </template>
        </o-table>
        <o-modal :can-cancel="['escape', 'outside', 'button']" v-model:active="isPreviewModalActive"
            class="preview-modal" has-modal-card>
            <file-preview-modal :file="currentPreviewFile" />
        </o-modal>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import { fetchMessages, getLabelInfo, get_thread_summary, mark_threads_as_read, mark_threads_as_unread, trash_threads } from "../../modules/emails/gmail_api"
import FilePreviewModal from './../../modules/emails/components/modals/FilePreviewModal'
import EmailsListControlsBar from './EmailsListControlsBar.vue'

const now = new Date()

export default {
    components: { EmailsListControlsBar, FilePreviewModal },
    computed: {
        ...mapGetters([
            'emails_get_messages',
            'emails_get_pagination',
            'emails_label',
            'emails_labels',
            'emails_query',
        ]),
        isSelectAllActive() {
            const cl = this.table.checkedRows.length, ml = this.messages.length
            return cl > 0 && cl === ml
        },
        isSelectAllIndeterminate() {
            const cl = this.table.checkedRows.length, ml = this.messages.length
            return cl > 0 && cl !== ml
        },
        label() {
            return this.emails_label.split(',')[0]
        },
        markreadEnabled() {
            return this.table.checkedRows.length && this.table.checkedRows.every(m => m.messages.some(m => m.label_ids.includes('UNREAD')))
        },
        markunreadEnabled() {
            return this.table.checkedRows.length && !this.markreadEnabled
        },
        messages() {
            return [...this.emails_get_messages]
        },
        pagination() {
            return { ...this.emails_get_pagination }
        },
        total() {
            const label_info = this.emails_labels[this.label] || {}
                , inbox_label_info = this.emails_labels.INBOX || {}
            return (this.label === 'UNREAD' && inbox_label_info.threadsUnread) || label_info.threadsTotal || null
        },
    },
    data() {
        return {
            currentPreviewFile: "",
            isPreviewModalActive: false,
            pagination_loading: false,
            table: {
                checkedRows: [],
            },
        }
    },
    methods: {
        ...mapActions([
            'emails_set_labels',
            'emails_set_messages',
            'emails_set_pagination',
            'emails_set_is_signed_in'
        ]),
        async getMessages() {
            const labelIds = this.emails_label.split(',')
            const params = {
                includeSpamTrash: false,
                labelIds,
                maxResults: this.pagination.perPage,
                userId: "me",
            }
            if (!!this.emails_query) {
                params.q = this.emails_query
            }
            if (this.pagination.pageToken) {
                params.pageToken = this.pagination.pageToken
            }
            this.pagination_loading = true
            return fetchMessages(params).then(res => {
                const { messages } = res
                this.emails_set_messages(messages)
                this.pagination_loading = false
                return res
            }).then((res) => (this.pagination_loading = false, res))
        },
        getDate(dateString) {
            const date = new Date(dateString)
            const hours = (now.getTime() - date.getTime()) / (60 * 60 * 1000)
            if (hours < 24) {
                return moment(dateString).format('hh:mm A')
            }
            return moment(dateString).format('MMM DD')
        },
        get_files(messages) {
            const files = [],
                total = 3
            let cont = 0
            messages.forEach(m => {
                if (m.files) {
                    m.files.forEach(file => {
                        if (cont < total) {
                            if (file.disposition === 'attachment') {
                                files.push(file)
                                cont++
                            }
                        } else {
                            return
                        }
                    })
                }
            })
            return files
        },
        getPagination(page) {
            const { currentPage, } = this.pagination
            if (page < currentPage) {
                return this.getPaginationPrevious()
            }
            if (page === 1 || page === currentPage) {
                return this.pagination
            } else {
                const pagination = this.pagination, key = this.pagination.pageToken || 'initial'
                    , current_object = { ...this.pagination.navigationObject[key] }
                if (!current_object.nextToken) {
                    return {
                        ...pagination,
                        currentPage: page,
                    }
                }
                return this.getPaginationNext()
            }
        },
        getPaginationNext() {
            const key = this.pagination.pageToken || 'initial'
                , current_object = this.pagination.navigationObject[key]
            const nextPagination = { ...this.emails_get_pagination.navigationObject[current_object.nextToken] }
            const { currentPage, pageToken } = nextPagination
            return { ...this.emails_get_pagination, currentPage, pageToken }
        },
        getPaginationPrevious() {
            const key = this.pagination.pageToken || 'initial'
                , current_object = this.pagination.navigationObject[key]
            const previousPagination = { ...this.emails_get_pagination.navigationObject[current_object.previousToken || 'initial'] }
            const { currentPage, pageToken } = previousPagination
            return { ...this.emails_get_pagination, currentPage, pageToken }
        },
        handle(type) {
            let threads_ids = []
            switch (type) {
                case 'delete':
                    threads_ids = this.table.checkedRows.map(m => m.message_id)
                    this.emails_set_messages([...this.messages.filter(m => !threads_ids.includes(m.message_id))])
                    this.reversible_action(threads_ids, (() => Promise.resolve(this.handlePaginationChange(this.emails_get_pagination.currentPage))), 'Conversation moved to trash', { undone_handler: trash_threads })
                    break
                case 'markread':
                    threads_ids = this.table.checkedRows.filter(m => !this.isRead(m)).map(item => item.message_id)
                    mark_threads_as_read(threads_ids).then(() => this.handlePaginationChange(this.emails_get_pagination.currentPage), this.reversible_action(threads_ids, (threads_ids => mark_threads_as_unread(threads_ids).then(() => Promise.resolve(this.handlePaginationChange(this.emails_get_pagination.currentPage)))), 'Conversarion marked as read'))
                    break
                case 'markunread':
                    threads_ids = this.table.checkedRows.filter(m => this.isRead(m)).map(item => item.message_id)
                    mark_threads_as_unread(threads_ids).then(() => this.handlePaginationChange(this.emails_get_pagination.currentPage), this.reversible_action(threads_ids, (threads_ids => mark_threads_as_read(threads_ids).then(() => Promise.resolve(this.handlePaginationChange(this.emails_get_pagination.currentPage)))), 'Conversarion marked as unread'))
                    break
                case "refresh":
                    this.init()
                    break
                case "selectToggle":
                    if (this.table.checkedRows.length) {
                        if (this.table.checkedRows.length === this.messages.length) {
                            this.table.checkedRows = []
                        } else {
                            this.table.checkedRows = [...this.messages]
                        }
                    } else {
                        this.table.checkedRows = [...this.messages]
                    }
                    break
                default:
                    break
            }
        },
        handle_open_file(file) {
            this.currentPreviewFile = file
            this.isPreviewModalActive = true
        },
        handlePaginationChange: _.debounce(function (page) {
            const { currentPage, } = this.pagination
            const p = this.getPagination(page)
            this.emails_set_pagination(p)
            this.getMessages().then(res => {
                for (let index = 0; index < this.messages.length; index++) {
                    const m = this.messages[index]
                    get_thread_summary(m.id).then(res => this.updateItem(m, res))

                }
                this.updatePagination({ ...res, currentPage, page })
            })
        }, 250),
        handleRowClick(row) {
            this.$router.push({ name: "emails.view", params: { message_id: row.id } })
        },
        init() {
            const label = this.label === 'UNREAD' && 'INBOX' || this.label
            getLabelInfo(label).then(label_info => {
                const labels = { ...this.emails_labels }
                labels[label_info.id] = labels[label_info.data.id] || label_info
                labels[label_info.id].messagesTotal = label_info.data.messagesTotal
                labels[label_info.id].messagesUnread = label_info.data.messagesUnread
                labels[label_info.id].threadsTotal = label_info.data.threadsTotal
                labels[label_info.id].threadsUnread = label_info.data.threadsUnread
                this.emails_set_labels(labels)
                this.emails_set_pagination({ ...this.emails_get_pagination, total: this.total })
            })
            this.handlePaginationChange(this.pagination.currentPage)
        },
        isRead(message) {
            return (Array.isArray(message.messages)) && !message.messages.some(m => m.label_ids.includes('UNREAD'))
        },
        reversible_action(data, callback, message, params) {
            const { actionText = 'Undo', position = 'bottom-left', type = 'warning' } = params || {}
            const timeout = 3500
            let undone = false
            this.table.checkedRows = []
            setTimeout(() => {
                if (!undone && typeof params.undone_handler === 'function') {
                    params.undone_handler(data)
                }
            }, timeout);
            this.$swal.fire({
                title: actionText,
                text: message,
                icon: type,
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes',
                onAction: () => {
                    undone = true
                    typeof callback === 'function' && callback(data).then(() => this.$swal.fire({
                        timer: 1500,
                        text: 'Action undone',
                        position: 'bottom-left',
                    }))
                },
                position: position,
            })
        },
        rowClass(row) {
            return this.isRead(row) && 'read'
        },
        updateItem(message, item) {
            message.data = item.data
            message.date = item.date
            message.from = item.from
            message.from_array = item.from_array
            message.from_names_count = item.from_names_count
            message.messages = item.messages
            message.message_id = item.message_id
            message.subject = item.subject
            message.files = this.get_files(item.messages)
            _.debounce(() => {
                if (!!this.emails_query) {
                    this.messages.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                }
                this.emails_set_messages(this.messages)
                this.$forceUpdate()
            }, 250)()
        },
        updatePagination({ nextPageToken, page }) {
            const pagination = { ...this.emails_get_pagination }
            !pagination.navigationObject[nextPageToken] && (pagination.navigationObject[nextPageToken] = {
                currentPage: page + 1,
                nextToken: null,
                pageToken: nextPageToken,
                previousToken: pagination.pageToken,
            })
            const currrent_object = pagination.navigationObject[pagination.pageToken || 'initial']
            currrent_object.nextToken = currrent_object.nextToken || nextPageToken
            this.emails_set_pagination(pagination)
        },
    },
    mounted() {
        this.init()
        if (this.messages.length > 0) {
            this.emails_set_is_signed_in(true)
        }
    },
    watch: {
        emails_label() {
            this.init()
        },
        emails_query() {
            this.handlePaginationChange(this.pagination.currentPage)
        }
    },
}
</script>

<style lang="sass" scoped>
.title
  font-size: 1rem

.eml-emails-list
  .eml-emails-list-table
    &.queried
      .snippet
        b
          background: orange
    .table-wrapper
      table
        thead
          display: none
        tr
          background: #fff
          box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.1)
          td
            .title
              color: #202124
              font-size: 0.875rem
            &.column-from
              .count
                color: #5f6368
            &:last-child
              text-align: right
              white-space: nowrap
        tr.read
          background-color: rgba(242, 245, 245, 0.8)
          .title
            font-weight: 400
    .snippet
      color: #5f6368
      text-overflow: ellipsis
    .msg-attachments
      display: flex
      margin-top: 9px
      .msg-attachment
        align-items: center
        border-radius: 15px
        box-shadow: inset 0 0 0 1px rgba(100,121,143,0.302)
        box-sizing: border-box
        color: #5f6368
        display: flex
        height: 30px
        justify-content: center
        margin-right: 8px
        max-width: 160px
        min-width: 64px
        padding: 0 12px
        a
          overflow: hidden
          text-overflow: ellipsis
          white-space: nowrap
</style>